import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api';
import { StoreContact } from '../types';

/**
 * Ko prima poruke objekta — poslovođa iz šifarnika, ili zamenik dok je
 * poslovođa na godišnjem.
 *
 * Zamenik se drži ovde, a ne u ERP šifarniku, jer traje dve nedelje: menjanje
 * vezanog objekta u šifarniku bi poslovođi usput oduzelo i pristup njegovom
 * ekranu. Ovde se menja samo kome ide poruka.
 */
export default function StoreContacts() {
  const [stores, setStores] = useState<StoreContact[]>([]);
  const [openCode, setOpenCode] = useState<string | null>(null);
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [until, setUntil] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setStores(await api.storeContacts());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const open = (store: StoreContact) => {
    setOpenCode(store.code);
    setPhone(store.replacementPhone ?? '');
    setName(store.replacementName ?? '');
    setUntil(store.replacementUntil?.slice(0, 10) ?? '');
    setError(null);
  };

  const save = async (code: string, clear = false) => {
    setBusy(true);
    try {
      await api.setStoreContact(code, clear
        ? { phone: '' }
        : { phone: phone.trim(), name: name.trim(), until: until || null });
      setOpenCode(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (stores.length === 0) return null;

  return (
    <section className="panel">
      <h2>Poruke objektima</h2>
      <p className="note">
        Poslovođa dobija Viber čim se tura sačuva: šta mu stiže i šta da spremi
        za utovar, po nalozima. Kad je na godišnjem, ovde se upiše zamenik —
        pristup njegovom ekranu se time ne dira.
      </p>

      {error && <div className="health health--bad">{error}</div>}

      <ul className="storelist">
        {stores.map((store) => (
          <li key={store.code}>
            <span className="storelist__where">
              {store.name}
              <span className="muted">
                {' '}· {store.activeName ?? 'nema kontakt'}
                {store.activePhone && ` · ${store.activePhone}`}
              </span>
              {store.replacementPhone && (
                <span className="storelist__why muted">
                  menja {store.managerName ?? 'poslovođu'}
                  {store.replacementUntil && ` do ${store.replacementUntil.slice(0, 10)}`}
                </span>
              )}
            </span>

            {store.replacementPhone
              ? <span className="flag flag--warn">zamenik</span>
              : store.activePhone
                ? <span className="flag flag--ok">poslovođa</span>
                : <span className="flag flag--bad">bez kontakta</span>}

            <span className="stop__actions">
              <button type="button" onClick={() => open(store)}>
                {store.replacementPhone ? 'Promeni' : 'Zamenik'}
              </button>
              {store.replacementPhone && (
                <button type="button" disabled={busy} onClick={() => void save(store.code, true)}>
                  Vrati poslovođi
                </button>
              )}
            </span>

            {openCode === store.code && (
              <div className="stop__cargo">
                <div className="probaporuka__red">
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="broj zamenika"
                    aria-label="Broj zamenika"
                  />
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="ime zamenika"
                    aria-label="Ime zamenika"
                  />
                  <input
                    type="date"
                    value={until}
                    onChange={(e) => setUntil(e.target.value)}
                    aria-label="Važi do"
                  />
                  <button
                    type="button"
                    className="primary"
                    disabled={busy || phone.trim().length === 0}
                    onClick={() => void save(store.code)}
                  >
                    Sačuvaj
                  </button>
                  <button type="button" onClick={() => setOpenCode(null)}>Odustani</button>
                </div>
                <p className="note">
                  Datum je poslednji dan važenja. Prazno znači „dok se ne vrati
                  ručno" — za slučaj kad se ne zna kad se poslovođa vraća.
                </p>
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
